/* eslint-disable no-console -- browser error handler, no Winston available */
import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';

/**
 * Guard against a reload loop for repeated stale-chunk errors. sessionStorage
 * survives page reloads in the same tab, so reload at most once per window —
 * this still allows recovery if another deploy lands later in the session,
 * but never spins on a genuinely broken bundle.
 */
const RELOAD_GUARD_KEY = 'sellerSage:staleChunkReloadAt';
const RELOAD_GUARD_WINDOW_MS = 30_000;

@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  private ngZone = inject(NgZone);

  handleError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);

    // Chunk load failure — Angular's lazy chunk fetch failed.
    // This happens when a user has the app open during a deploy and
    // navigates to a route whose lazy chunk has been replaced. With an SPA
    // catch-all serving index.html for missing files, the browser rejects the
    // module script on MIME type, so match those messages too.
    const isStaleChunk =
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Failed to load module script') ||
      message.includes('MIME type of "text/html"');

    if (!isStaleChunk) {
      // Re-throw all other errors to console for debugging
      console.error('[UnhandledError]', error);
      return;
    }

    console.error('[ChunkLoadError] Stale version detected, reloading...', error);

    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - lastReloadAt <= RELOAD_GUARD_WINDOW_MS) {
      return;
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));

    // Attempt recovery: full page reload gets the new index.html
    // which references the new chunk hashes.
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    });
  }
}
